import { Search, HelpCircle, MessageCircle, ShieldCheck } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { PageHero } from "@/components/PageHero";
import { TravelSearchWidget } from "@/components/TravelSearchWidget";
import { SectionTitle } from "@/components/SectionTitle";
import { CTASection } from "@/components/CTASection";
import { Reveal } from "@/components/Reveal";
import { waLink, WA_MESSAGES } from "@/lib/site";

const STEPS = [
  { icon: Search, title: "Como pesquisar", text: "Informe destino, datas e número de viajantes na busca acima para ver as opções disponíveis." },
  { icon: HelpCircle, title: "Como tirar dúvidas", text: "Surgiu alguma dúvida durante a pesquisa? Fale com a nossa equipe e receba orientação personalizada." },
  { icon: MessageCircle, title: "Como falar com a Miríade", text: "O WhatsApp está sempre disponível para você decidir com mais tranquilidade e segurança." },
  { icon: ShieldCheck, title: "Cuidados antes de viajar", text: "Confirme documentos, prazos e detalhes da viagem com a nossa equipe antes de fechar." },
];

export default function BuscarViagens() {
  useSeo({
    title: "Buscar Viagens | Miríade Turismo",
    description:
      "Busque sua próxima viagem e conte com a Miríade para ajudar você a escolher com mais segurança. Pesquise destinos, datas e opções de viagem.",
  });

  return (
    <>
      <PageHero
        overline="Planeje com a Miríade"
        title="Busque sua próxima viagem"
        subtitle="Encontre opções de viagem e conte com a Miríade para ajudar você a escolher com mais segurança."
      />

      <section data-testid="search-page-widget" className="px-4 py-14 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <TravelSearchWidget />
          <div className="mt-8 rounded-3xl border border-brand-light bg-brand-light/50 p-6 text-center">
            <p className="font-heading text-lg font-medium text-ink">
              Precisa de ajuda para decidir? Fale com uma consultora.
            </p>
            <a
              href={waLink(WA_MESSAGES.consultant)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="search-page-consultant-btn"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 text-sm font-semibold text-white transition-all hover:scale-[1.02] hover:bg-[#1ebe5d]"
            >
              <MessageCircle size={18} /> Falar com uma consultora
            </a>
          </div>
        </div>
      </section>

      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionTitle center overline="Simples assim" title="Como funciona a sua busca" />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div data-testid={`search-step-${i}`} className="h-full rounded-3xl border border-border bg-white p-8 shadow-soft">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-light">
                    <s.icon size={26} className="text-brand" />
                  </div>
                  <h3 className="mt-6 font-heading text-lg font-semibold text-ink">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink-soft">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        title="Ainda em dúvida sobre o destino?"
        text="Conte para a Miríade o que você imagina para a viagem e ajudamos a encontrar o melhor caminho."
      />
    </>
  );
}
